/**
 * Stickers.
 *
 * Stickers are the decoration that actually suits a three-year-old: two taps - pick one, put it
 * somewhere - and the result always looks deliberate. Freehand marks need a steady hand and a plan;
 * a sticker needs neither, and she still made the choice of which and where.
 *
 * Deliberately not the mission items. A bone she is asked to fetch and a bone she can stick on the
 * bonnet would be the same picture meaning two different things, which is one ambiguity too many in a
 * game with no words.
 */

import { ownedPrizeIds, prizeIds } from '../core/game/unlock-rules.js';

export interface StickerDef {
  readonly id: string;
  /** Parent-facing only. */
  readonly label: string;
}

export const STICKERS: readonly StickerDef[] = [
  { id: 'star', label: 'star' },
  { id: 'heart', label: 'heart' },
  { id: 'paw', label: 'paw print' },
  { id: 'flower', label: 'flower' },
  { id: 'bolt', label: 'lightning bolt' },
  { id: 'smile', label: 'smiley face' },
  // The second row: bundle art refitted by scripts/build-flair.mjs. Balloons and a paw-with-a-heart,
  // and still no bone - that rule above holds however good the bundle's bones are.
  { id: 'balloon', label: 'balloon' },
  { id: 'heart-balloon', label: 'heart balloon' },
  { id: 'star-balloon', label: 'star balloon' },
  { id: 'paw-heart', label: 'paw with a heart' },
];

/**
 * The ones in her tray today: everything the prize table does not hand out, plus every set she has earned.
 *
 * Stickers are the best prize in the game and always were - they land on her own drawing, she chooses where,
 * they survive in the save, and a new one changes what she can *make* rather than what a dog is wearing. So
 * the tray starts smaller than the pack, and grows.
 *
 * Which stickers are prizes is decided in one place only, the prize table
 * (`src/core/game/unlock-rules.ts`), and a sticker nothing grants is free - so adding art to the tray can
 * never accidentally lock it away behind a prize that does not exist.
 *
 * This filters the *tray*, never the drawing: a sticker she has already stuck on a car is drawn from the
 * saved car (`applyStickers`), which does not consult this. There is no way to explain a sticker
 * disappearing off a car she finished.
 */
export function availableStickers(unlocks: readonly string[]): readonly StickerDef[] {
  const prizes = new Set(prizeIds('sticker-set'));
  const earned = new Set(ownedPrizeIds('sticker-set', unlocks));
  return STICKERS.filter((sticker) => !prizes.has(sticker.id) || earned.has(sticker.id));
}

/** Default size as a fraction of the template width. Big enough to be unmistakably a sticker. */
export const STICKER_SCALE = 0.15;

export function stickerById(id: string): StickerDef | null {
  return STICKERS.find((sticker) => sticker.id === id) ?? null;
}

export function stickerUrl(id: string, pack = 'patrol'): string {
  return `${import.meta.env.BASE_URL}content/packs/${pack}/stickers/${id}.svg`;
}

const cache = new Map<string, Promise<SVGSVGElement | null>>();

/**
 * The sticker's own SVG, parsed once and cached. Callers clone it - the same star may end up on the
 * car several times, and in the tray as well.
 */
export function loadStickerSvg(id: string): Promise<SVGSVGElement | null> {
  let pending = cache.get(id);
  if (pending === undefined) {
    pending = fetch(stickerUrl(id))
      .then(async (response) => {
        if (!response.ok) throw new Error(`sticker "${id}": HTTP ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'image/svg+xml');
        if (parsed.querySelector('parsererror') !== null) return null;
        return parsed.documentElement as unknown as SVGSVGElement;
      })
      .catch((error: unknown) => {
        console.warn(`[pawp] could not load sticker "${id}"`, error);
        cache.delete(id);
        return null;
      });
    cache.set(id, pending);
  }
  return pending;
}

/** The sticker's intrinsic size, for centring it on the point she tapped. */
export function stickerViewBox(svg: SVGSVGElement): { width: number; height: number } {
  const [, , w, h] = (svg.getAttribute('viewBox') ?? '0 0 200 200').trim().split(/[\s,]+/).map(Number);
  return {
    width: Number.isFinite(w) && (w as number) > 0 ? (w as number) : 200,
    height: Number.isFinite(h) && (h as number) > 0 ? (h as number) : 200,
  };
}
